import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store/store";
import { sessionStorageService } from "@utils/storageService/StorageService";
import { AppTheme, themeMap } from "./themes";

type ThemeName = keyof typeof themeMap;
interface LayoutType {
  currentBreakpoint: string;
  isLarge: boolean;
  theme: AppTheme;
  themeName: ThemeName;
}

const persistedCurrentTheme =
  (sessionStorageService.get("appState.theme") as ThemeName) ?? "lightTheme";

const initialState: LayoutType = {
  currentBreakpoint: "",
  isLarge: false,
  theme: themeMap[persistedCurrentTheme],
  themeName: persistedCurrentTheme,
};

export const layoutSlice = createSlice({
  name: "layout",
  initialState,
  reducers: {
    updateCurrentBreakpoint: (state, action: PayloadAction<string>) => {
      sessionStorageService.set("appState.breakpoint", action.payload);
      return {
        ...state,
        currentBreakpoint: action.payload,
        isLarge: action.payload === "lg" || action.payload === "xl",
      };
    },
    updateTheme: (state, action: PayloadAction<ThemeName>) => {
      sessionStorageService.set("appState.theme", action.payload);
      return {
        ...state,
        themeName: action.payload,
        theme: themeMap[action.payload] ?? themeMap.lightTheme,
      };
    },
    toggleTheme: (state) => {
      const nextTheme =
        state.themeName === "lightTheme" ? "darkTheme" : "lightTheme";
      sessionStorageService.set("appState.theme", nextTheme);
      state.themeName = nextTheme;
      state.theme = themeMap[nextTheme];
    },
  },
});

export const selectCurrentBreakpoint = (state: RootState): string =>
  state.layout.currentBreakpoint;
export const selectIsLarge = (state: RootState): boolean =>
  state.layout.isLarge;
export const selectCurrentTheme = (state: RootState): AppTheme =>
  state.layout.theme;
export const selectCurrentThemeName = (state: RootState): ThemeName =>
  state.layout.themeName;
export const { updateCurrentBreakpoint, updateTheme, toggleTheme } =
  layoutSlice.actions;

export default layoutSlice.reducer;
